import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "@/core/lib/api";
import type { ProfileData } from "@/core/schema/profile-schema";

class ProfileApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ProfileApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ProfileApiError(message, res.status);
  }
  return data as T;
}

export async function getProfile(): Promise<ProfileData> {
  return request<ProfileData>("/api/profile");
}

/**
 * Patches the owner's profile. The General and Company screens each send only
 * the fields they own; the backend upserts just those, so partial saves are
 * safe and always return the full, normalized record.
 */
export async function updateProfile(data: Partial<ProfileData>): Promise<ProfileData> {
  return request<ProfileData>("/api/profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/**
 * Uploads a signature image (a PNG exported from the draw/type canvas, or an
 * image file the user picked) to the backend, which stores it in R2 and saves
 * the URL on the owner's profile. Returns the public URL.
 *
 * Like avatar upload, this can't reuse the JSON `request` helper — the browser
 * must set the multipart boundary itself, so we don't set Content-Type.
 */
export async function uploadSignature(file: File | Blob): Promise<{ signature: string }> {
  const body = new FormData();
  body.append("file", file, "signature.png");

  let res: Response;
  try {
    res = await apiFetch(apiUrl("/api/profile/signature"), {
      method: "POST",
      credentials: "include",
      body,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ProfileApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Upload failed (${res.status})`;
    throw new ProfileApiError(message, res.status);
  }
  return data as { signature: string };
}

/** Clears the stored signature (deletes the R2 object server-side). */
export async function deleteSignature(): Promise<void> {
  await request<void>("/api/profile/signature", { method: "DELETE" });
}

export { ProfileApiError };
